import React, { useState } from 'react'
import { Link, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { useBlog } from '../context/BlogContext'
import { urlFor } from '../lib/sanityClient'

const CATEGORY_LABELS = {
  all: 'All Posts',
  'industry-news': 'Industry News',
  'product-updates': 'Product Updates',
  'guides-tips': 'Guides & Tips',
  'case-studies': 'Case Studies',
  'company-news': 'Company News',
  technology: 'Technology',
}

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function PostCard({ post, index }) {
  const imageUrl = post.coverImage
    ? urlFor(post.coverImage).width(600).height(340).auto('format').fit('crop').url()
    : null

  return (
    <motion.article
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.97 }}
      transition={{ duration: 0.35, delay: index * 0.06 }}
      className="group card overflow-hidden cursor-pointer flex flex-col"
    >
      <Link to={`/blog/${post.slug}`} className="contents">
        {/* Cover image */}
        <div className="relative overflow-hidden bg-surface-2 h-48">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={post.coverImage?.alt || post.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-accent/10 to-accent/5">
              <svg
                className="w-10 h-10 text-accent/30"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={1}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
            </div>
          )}
          {post.category && (
            <span className="absolute top-3 left-3 badge bg-white/90 dark:bg-slate-900/90 text-accent backdrop-blur-sm text-xs">
              {CATEGORY_LABELS[post.category] || post.category}
            </span>
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col flex-1 p-6">
          <div className="flex items-center gap-3 text-xs text-secondary mb-3">
            <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
            {post.estimatedReadingTime && (
              <>
                <span className="w-1 h-1 rounded-full bg-border-strong" />
                <span>{post.estimatedReadingTime} min read</span>
              </>
            )}
          </div>

          <h2 className="text-base md:text-lg font-semibold text-primary leading-snug mb-2 group-hover:text-accent transition-colors duration-200">
            {post.title}
          </h2>

          {post.excerpt && (
            <p className="text-secondary text-sm leading-relaxed line-clamp-3 flex-1">
              {post.excerpt}
            </p>
          )}

          <div className="flex items-center justify-between mt-5 pt-4 border-t border-border-subtle">
            {post.author ? (
              <div className="flex items-center gap-2">
                {post.authorImage ? (
                  <img
                    src={urlFor(post.authorImage).width(32).height(32).fit('crop').url()}
                    alt={post.author}
                    className="w-6 h-6 rounded-full object-cover border border-border-subtle"
                  />
                ) : (
                  <div className="w-6 h-6 rounded-full bg-accent/15 flex items-center justify-center">
                    <span className="text-accent text-xs font-semibold">
                      {post.author.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="text-xs text-secondary">{post.author}</span>
              </div>
            ) : (
              <span />
            )}
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent group-hover:gap-2 transition-all duration-200">
              Read
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5-5 5M6 12h12" />
              </svg>
            </span>
          </div>
        </div>
      </Link>
    </motion.article>
  )
}

export default function Blog() {
  const { posts, loading, hasActivePosts } = useBlog()
  const [activeCategory, setActiveCategory] = useState('all')

  if (!loading && !hasActivePosts) {
    return <Navigate to="/" replace />
  }

  const categories = ['all', ...Array.from(new Set(posts.map((p) => p.category).filter(Boolean)))]

  const filtered =
    activeCategory === 'all' ? posts : posts.filter((p) => p.category === activeCategory)

  return (
    <main className="min-h-screen bg-surface-0">
      {/* Hero */}
      <section className="pt-32 pb-16 px-4 md:px-8 lg:px-16 bg-surface-1 dark:bg-surface-1 border-b border-border-subtle">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span className="section-label">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
              </svg>
              Blog
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-primary mt-2 mb-4">
              Insights & Updates
            </h1>
            <p className="text-secondary text-lg max-w-2xl">
              The latest thinking from the Clutch team — industry news, product updates,
              practical guides and more.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding">
        <div className="section-container">
          {/* Category filter */}
          {categories.length > 1 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="flex flex-wrap gap-2 mb-10"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer border ${
                    activeCategory === cat
                      ? 'bg-accent text-white border-accent shadow-glow-sm'
                      : 'bg-surface-card text-secondary border-border-medium hover:border-accent hover:text-accent'
                  }`}
                >
                  {CATEGORY_LABELS[cat] || cat}
                </button>
              ))}
            </motion.div>
          )}

          {/* Loading skeleton */}
          {loading && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card overflow-hidden animate-pulse">
                  <div className="h-48 bg-surface-2" />
                  <div className="p-6 space-y-3">
                    <div className="h-3 bg-surface-2 rounded w-1/3" />
                    <div className="h-5 bg-surface-2 rounded w-5/6" />
                    <div className="h-3 bg-surface-2 rounded w-full" />
                    <div className="h-3 bg-surface-2 rounded w-4/5" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Posts grid */}
          {!loading && (
            <>
              {filtered.length === 0 ? (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-20 text-secondary"
                >
                  <svg
                    className="w-12 h-12 mx-auto mb-4 text-border-strong"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={1.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="text-lg font-medium">No posts in this category yet.</p>
                </motion.div>
              ) : (
                <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <AnimatePresence mode="popLayout">
                    {filtered.map((post, i) => (
                      <PostCard key={post._id} post={post} index={i} />
                    ))}
                  </AnimatePresence>
                </motion.div>
              )}
            </>
          )}
        </div>
      </section>
    </main>
  )
}
